import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { colors } from '@tabbio/tokens';
import { TabbioAIIcon } from '../common/TabbioAIIcon';
import { WebPageHeader } from '../common/WebPageHeader';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { useTheme } from '../../hooks/useTheme';
import {
  CANDIDATES,
  SHORTLISTED,
  SHORTLIST_STATUS_COLORS,
  CANDIDATE_FILTER_GROUPS,
  type CandidateAvailability,
  type CandidateItem,
  type FilterGroup,
  type ShortlistedCandidate,
} from '../../lib/candidates-mock-data';

type Tab = 'browse' | 'shortlisted';
type CandidateFilters = Record<string, string | null>;

const BRAND = colors.primary;

function getAvailabilityLabelKey(availability: CandidateAvailability) {
  switch (availability) {
    case 'immediate':
      return 'candidates.filters.immediate';
    case '2weeks':
      return 'candidates.filters.twoWeeks';
    case '1month':
      return 'candidates.filters.oneMonth';
    case '3months':
      return 'candidates.filters.threeMonths';
    default:
      return 'candidates.filters.availability';
  }
}

function parseYears(experience: string) {
  const match = experience.match(/\d+/);
  return match ? Number.parseInt(match[0], 10) : 0;
}

function matchesExperienceFilter(experience: string, filter: string | null) {
  if (!filter) return true;
  const years = parseYears(experience);

  switch (filter) {
    case '0-2':
      return years <= 2;
    case '3-5':
      return years >= 3 && years <= 5;
    case '6-10':
      return years >= 6 && years <= 10;
    case '10+':
      return years >= 10;
    default:
      return true;
  }
}

function matchesSearch(item: CandidateItem | ShortlistedCandidate, query: string) {
  if (!query.trim()) return true;
  const normalizedQuery = query.trim().toLowerCase();
  return (
    item.name.toLowerCase().includes(normalizedQuery) ||
    item.title.toLowerCase().includes(normalizedQuery) ||
    item.location.toLowerCase().includes(normalizedQuery)
  );
}

function FilterDropdown({
  group,
  selected,
  onSelect,
}: {
  group: FilterGroup;
  selected: string | null;
  onSelect: (value: string | null) => void;
}) {
  const { t } = useTranslation('seeker');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selectedOption = group.options.find((option) => option.value === selected);

  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className={selected ? 'tabbio-chip active' : 'tabbio-chip'}
      >
        {selectedOption ? t(selectedOption.labelKey) : t(group.labelKey)}
        <MaterialSymbol
          name="keyboard_arrow_down"
          size={16}
          style={{
            color: 'var(--text-secondary)',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 150ms',
          }}
        />
      </button>

      {open ? (
        <div
          className="tabbio-menu show"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            insetInlineStart: 0,
            minWidth: 220,
          }}
        >
          {group.options.map((option) => {
            const isSelected = selected === option.value;
            return (
              <button
                key={option.value}
                type="button"
                className={isSelected ? 'tabbio-menu-item checked' : 'tabbio-menu-item'}
                onClick={() => {
                  onSelect(isSelected ? null : option.value);
                  setOpen(false);
                }}
              >
                <span className="tabbio-menu-label">{t(option.labelKey)}</span>
                {isSelected ? (
                  <MaterialSymbol name="check" className="tabbio-menu-check" />
                ) : null}
              </button>
            );
          })}
          <div className="tabbio-menu-divider" />
          <button
            type="button"
            className="tabbio-menu-item"
            onClick={() => {
              onSelect(null);
              setOpen(false);
            }}
            style={{ justifyContent: 'center' }}
          >
            <span
              className="tabbio-menu-label"
              style={{
                textAlign: 'center',
                color: 'var(--text-secondary)',
                fontWeight: 500,
              }}
            >
              {t('jobs.filters.clear')}
            </span>
          </button>
        </div>
      ) : null}
    </div>
  );
}

function BrowseCandidateCard({
  item,
  isLast,
  onClick,
  t,
}: {
  item: CandidateItem;
  isLast: boolean;
  onClick: () => void;
  t: (key: string) => string;
}) {
  const { theme, isDark } = useTheme();
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: '100%',
        border: 'none',
        borderBottom: isLast ? 'none' : `1px solid ${theme.borderSubtle}`,
        backgroundColor: hovered
          ? isDark
            ? theme.surfaceSecondary
            : 'rgba(0,0,0,0.015)'
          : 'transparent',
        padding: '16px',
        textAlign: 'start',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 16,
        cursor: 'pointer',
        fontFamily: 'inherit',
        transition: 'background-color 150ms ease',
      }}
    >
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: 999,
          background: isDark
            ? `linear-gradient(180deg, ${theme.surfaceSecondary}, ${theme.surfaceTertiary})`
            : `linear-gradient(180deg, ${colors.gray[100]}, ${colors.gray[200]})`,
          border: `1px solid ${theme.borderSubtle}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          fontSize: 14,
          fontWeight: 700,
          color: isDark ? theme.textSecondary : colors.gray[600],
        }}
      >
        {item.initials}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: 2,
              }}
            >
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 17,
                  fontWeight: 600,
                  letterSpacing: '-0.02em',
                  color: theme.text,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.name}
              </div>
            </div>

            <div
              style={{
                fontSize: 14,
                fontWeight: 500,
                color: theme.textSecondary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {item.title} · {item.location}
            </div>

            <div
              style={{
                marginTop: 8,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                maxWidth: '92%',
                borderRadius: 999,
                padding: '6px 10px',
                border: `1px solid ${theme.borderSubtle}`,
                backgroundColor: theme.surface,
                color: theme.textSecondary,
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              <span style={{ display: 'inline-flex', flexShrink: 0 }}>
                <TabbioAIIcon size={12} />
              </span>
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.experience} · {t(getAvailabilityLabelKey(item.availability))}
              </span>
            </div>

            <div style={{ marginTop: 8, fontSize: 12, fontWeight: 500, color: theme.textTertiary }}>
              {item.source} · {item.timeAgo}
            </div>
          </div>

          <div
            style={{
              alignSelf: 'flex-start',
              marginTop: 2,
              minHeight: 36,
              minWidth: 78,
              borderRadius: 999,
              padding: '8px 16px',
              border: `1px solid ${BRAND}`,
              backgroundColor: BRAND,
              color: '#ffffff',
              fontSize: 13,
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {t('candidates.view')}
          </div>
        </div>
      </div>
    </button>
  );
}

function ShortlistedCandidateCard({
  item,
  isLast,
  onClick,
  t,
}: {
  item: ShortlistedCandidate;
  isLast: boolean;
  onClick: () => void;
  t: (key: string, options?: Record<string, string | number>) => string;
}) {
  const { theme, isDark } = useTheme();
  const [hovered, setHovered] = useState(false);
  const statusColor = SHORTLIST_STATUS_COLORS[item.status];

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: '100%',
        border: 'none',
        borderBottom: isLast ? 'none' : `1px solid ${theme.borderSubtle}`,
        backgroundColor: hovered
          ? isDark
            ? theme.surfaceSecondary
            : 'rgba(0,0,0,0.015)'
          : 'transparent',
        padding: '16px',
        textAlign: 'start',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 16,
        cursor: 'pointer',
        fontFamily: 'inherit',
        transition: 'background-color 150ms ease',
      }}
    >
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: 999,
          background: isDark
            ? `linear-gradient(180deg, ${theme.surfaceSecondary}, ${theme.surfaceTertiary})`
            : `linear-gradient(180deg, ${colors.gray[100]}, ${colors.gray[200]})`,
          border: `1px solid ${theme.borderSubtle}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          fontSize: 14,
          fontWeight: 700,
          color: isDark ? theme.textSecondary : colors.gray[600],
        }}
      >
        {item.initials}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 2,
              }}
            >
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 17,
                  fontWeight: 600,
                  letterSpacing: '-0.02em',
                  color: theme.text,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.name}
              </div>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  flexShrink: 0,
                  borderRadius: 999,
                  padding: '4px 10px',
                  backgroundColor: `${statusColor}16`,
                  color: statusColor,
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {t(`candidates.status.${item.status}` as never)}
              </span>
            </div>

            <div
              style={{
                fontSize: 14,
                fontWeight: 500,
                color: theme.textSecondary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {item.title} · {item.location}
            </div>

            <div
              style={{
                marginTop: 8,
                display: 'inline-flex',
                alignItems: 'center',
                maxWidth: '92%',
                borderRadius: 999,
                padding: '6px 10px',
                border: `1px solid ${theme.borderSubtle}`,
                backgroundColor: theme.surface,
                color: theme.textSecondary,
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.experience} · {t(getAvailabilityLabelKey(item.availability))}
              </span>
            </div>

            <div style={{ marginTop: 8, fontSize: 12, fontWeight: 500, color: theme.textTertiary }}>
              {t('candidates.shortlistedAgo', { time: item.shortlistedAgo })}
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}

export function CandidatesFeedScreen({
  basePath,
}: {
  basePath: '/(app)/candidates' | '/(employer)/candidates';
}) {
  const { theme, isDark } = useTheme();
  const { t } = useTranslation('seeker');
  const router = useRouter();
  const [activeTab, setActiveTab] = useState<Tab>('browse');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<CandidateFilters>({
    experience: null,
    location: null,
    availability: null,
  });

  const filteredCandidates = useMemo(() => {
    return CANDIDATES.filter((candidate) => {
      if (!matchesSearch(candidate, searchQuery)) return false;
      if (!matchesExperienceFilter(candidate.experience, activeFilters.experience)) return false;
      if (activeFilters.location && candidate.location !== activeFilters.location) return false;
      if (activeFilters.availability && candidate.availability !== activeFilters.availability) {
        return false;
      }
      return true;
    });
  }, [activeFilters, searchQuery]);

  const filteredShortlisted = useMemo(() => {
    return SHORTLISTED.filter((candidate) => {
      if (!matchesSearch(candidate, searchQuery)) return false;
      if (!matchesExperienceFilter(candidate.experience, activeFilters.experience)) return false;
      if (activeFilters.location && candidate.location !== activeFilters.location) return false;
      if (activeFilters.availability && candidate.availability !== activeFilters.availability) {
        return false;
      }
      return true;
    });
  }, [activeFilters, searchQuery]);

  const items = activeTab === 'browse' ? filteredCandidates : filteredShortlisted;

  const handleBack = () => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      router.back();
      return;
    }
    router.push((basePath === '/(employer)/candidates' ? '/(employer)/chat' : '/(app)/chat') as never);
  };

  return (
    <div
      className="h-full overflow-y-auto"
      style={{
        backgroundColor: 'var(--bg)',
        fontFamily:
          "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
      }}
    >
      <div className="tabbio-svc-page">
        <WebPageHeader
          title={t('candidates.title')}
          subtitle={t('candidates.subtitle', {
            defaultValue: 'Discover and shortlist top talent',
          })}
          onBack={handleBack}
          hideBackOnMobile
        />

        {/* Search bar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            height: 48,
            borderRadius: 9999,
            paddingInline: 18,
            background: 'rgba(0,0,0,0.04)',
            marginBottom: 12,
          }}
        >
          <MaterialSymbol
            name="search"
            size={20}
            style={{ color: 'var(--text-secondary)', flexShrink: 0 }}
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder={t('candidates.searchPlaceholder')}
            style={{
              flex: 1,
              border: 'none',
              background: 'transparent',
              color: 'var(--text-primary)',
              fontSize: 15,
              outline: 'none',
              fontFamily: 'inherit',
            }}
          />
          {searchQuery.length > 0 ? (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              aria-label="Clear"
              style={{
                width: 28,
                height: 28,
                border: 0,
                borderRadius: '50%',
                background: 'transparent',
                color: 'var(--text-tertiary)',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <MaterialSymbol name="close" size={18} />
            </button>
          ) : null}
        </div>

        {/* Filters */}
        <div
          style={{
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            marginBottom: 16,
          }}
        >
          {CANDIDATE_FILTER_GROUPS.map((group) => (
            <FilterDropdown
              key={group.id}
              group={group}
              selected={activeFilters[group.id]}
              onSelect={(value) =>
                setActiveFilters((current) => ({
                  ...current,
                  [group.id]: value,
                }))
              }
            />
          ))}
        </div>

        {/* Tab strip */}
        <div
          role="tablist"
          style={{ display: 'flex', gap: 8, marginBottom: 16 }}
        >
          {(['browse', 'shortlisted'] as Tab[]).map((tab) => (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={activeTab === tab}
              onClick={() => setActiveTab(tab)}
              className={activeTab === tab ? 'tabbio-chip active' : 'tabbio-chip'}
              style={{ flex: 1, justifyContent: 'center' }}
            >
              {t(`candidates.tabs.${tab}`)}
              {tab === 'shortlisted' ? ` ${SHORTLISTED.length}` : ''}
            </button>
          ))}
        </div>

        {items.length === 0 ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 10,
              padding: '48px 16px',
              textAlign: 'center',
            }}
          >
            <span
              aria-hidden
              style={{ color: 'var(--text-tertiary)', marginBottom: 4 }}
            >
              <MaterialSymbol name="person_search" size={40} />
            </span>
            <div
              style={{
                fontSize: 18,
                fontWeight: 500,
                color: 'var(--text-primary)',
              }}
            >
              {activeTab === 'browse'
                ? t('candidates.noCandidates')
                : t('candidates.noShortlisted')}
            </div>
            <div
              style={{
                fontSize: 14,
                lineHeight: 1.5,
                color: 'var(--text-secondary)',
                maxWidth: 360,
              }}
            >
              {activeTab === 'browse'
                ? t('candidates.noCandidatesSubtitle')
                : t('candidates.noShortlistedSubtitle')}
            </div>
          </div>
        ) : (
          <div
            style={{
              background: 'rgba(0,0,0,0.04)',
              borderRadius: 20,
              overflow: 'hidden',
            }}
          >
            {activeTab === 'browse'
              ? (items as CandidateItem[]).map((item, index, array) => (
                  <BrowseCandidateCard
                    key={item.id}
                    item={item}
                    isLast={index === array.length - 1}
                    onClick={() => router.push(`${basePath}/${item.id}` as never)}
                    t={t}
                  />
                ))
              : (items as ShortlistedCandidate[]).map((item, index, array) => (
                  <ShortlistedCandidateCard
                    key={item.id}
                    item={item}
                    isLast={index === array.length - 1}
                    onClick={() => router.push(`${basePath}/${item.id}` as never)}
                    t={t as any}
                  />
                ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default CandidatesFeedScreen;
